import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const MyData = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showEditOptions, setShowEditOptions] = useState(false);

  useEffect(() => {
    fetchMyData();
  }, []);

  const fetchMyData = async () => {
    try {
      setLoading(true);
      // Get only data created by this user
      const response = await api.get(`/api/staffinfo/my-data`);
      setStaffData(response.data);
    } catch (error) {
      console.error('Error fetching my data:', error);
      setError('Failed to load your data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (staff) => {
    setSelectedStaff(staff);
    setShowEditOptions(false);
  };

  const handleEditOptions = (staff) => {
    setSelectedStaff(staff);
    setShowEditOptions(true);
  };

  const handleEditSection = (section) => {
    if (!selectedStaff) return;
    
    // All sections now go through the comprehensive wizard
    const route = '/comprehensive-staff-wizard';
    
    // Pass staff ID as query parameter for pre-filling and specify the section
    navigate(`${route}?edit=${selectedStaff.staff_id || selectedStaff.id}&section=${section}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <div className="text-gray-600">Loading your data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">My Data</h1>
          <p className="text-gray-600">View and manage your personal information</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Staff Records Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Staff List */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
              <h2 className="text-xl font-bold">My Records</h2>
              <p className="text-blue-100 mt-1">{staffData.length} record(s) found</p>
            </div>
            
            <div className="p-6">
              {staffData.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600">No records found</p>
                  <p className="text-sm text-gray-500">Start by adding your information</p>
                  <button
                    onClick={() => navigate('/comprehensive-staff-wizard')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Create First Record
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {staffData.map((staff, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{staff.fullname || 'Unnamed'}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            ID: {staff.staff_id || staff.id}
                          </p>
                          <p className="text-sm text-gray-500">
                            {staff.current_workposition && `Position: ${staff.current_workposition}`}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            Created: {staff.created_at ? new Date(staff.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <button
                            onClick={() => handleViewDetails(staff)}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200 transition-colors"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEditOptions(staff)}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded-md text-sm font-medium hover:bg-green-200 transition-colors"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Details/Edit Panel */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {selectedStaff && !showEditOptions ? (
              /* View Details Panel */
              <>
                <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white p-6">
                  <h2 className="text-xl font-bold">Staff Details</h2>
                  <p className="text-gray-100 mt-1">{selectedStaff.fullname}</p>
                </div>
                <div className="p-6 space-y-4">
                  <InfoCard label="Staff ID" value={selectedStaff.staff_id || selectedStaff.id} />
                  <InfoCard label="Full Name" value={selectedStaff.fullname} />
                  <InfoCard label="Date of Birth" value={selectedStaff.date_of_birth} />
                  <InfoCard label="Gender" value={selectedStaff.gender_name} />
                  <InfoCard label="Email" value={selectedStaff.email} />
                  <InfoCard label="Phone" value={selectedStaff.phone_number} />
                  <InfoCard label="Current Position" value={selectedStaff.current_workposition} />
                  <InfoCard label="Clinic" value={selectedStaff.clinic_name} />
                </div>
              </>
            ) : selectedStaff && showEditOptions ? (
              /* Edit Options Panel */
              <>
                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
                  <h2 className="text-xl font-bold">Edit Options</h2>
                  <p className="text-green-100 mt-1">Choose what to edit for {selectedStaff.fullname}</p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 gap-3">
                    <EditOptionCard
                    
                      title="Staff Information"
                      description="Personal details, contact info, education"
                      onClick={() => handleEditSection('staff')}
                    />
                    <EditOptionCard
                      
                      title="Job Information"
                      description="Position, clinic, work location"
                      onClick={() => handleEditSection('job')}
                    />
                    <EditOptionCard
                      
                      title="Work Experience"
                      description="Previous positions and experience"
                      onClick={() => handleEditSection('workexp')}
                    />
                    <EditOptionCard
                      
                      title="Training"
                      description="Training programs and certifications"
                      onClick={() => handleEditSection('training')}
                    />
                    <EditOptionCard
                      
                      title="Skills"
                      description="Language and computer skills"
                      onClick={() => handleEditSection('skills')}
                    />
                    <EditOptionCard
                      
                      title="Benefits"
                      description="Benefits and allowances"
                      onClick={() => handleEditSection('benefits')}
                    />
                  </div>
                </div>
              </>
            ) : (
              /* Default Panel */
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h5.586a1 1 0 00.707.293l5.414 5.414a1 1 0 00.293.707V19a2 2 0 002 2h11a2 2 0 002-2V5a2 2 0 00-2-2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select a Record</h3>
                  <p className="mt-1 text-sm text-gray-500">Choose a record to view details or edit</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const InfoCard = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100">
    <span className="text-sm font-medium text-gray-600">{label}:</span>
    <span className="text-sm text-gray-800">{value || 'Not provided'}</span>
  </div>
);

const EditOptionCard = ({ icon, title, description, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500"
  >
    <div className="flex items-start gap-3">
      <span className="text-2xl">{icon}</span>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-800">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </button>
);

export default MyData;
